import axios, { AxiosInstance } from 'axios';
import { apiSchema, ApiName, ApiInput, ApiOutput } from './api-schema';
import { ApiError, ApiErrorCode } from './api-types';

export class ApiClient {
  private axios: AxiosInstance;
  private tokenFn: () => Promise<string>;

  constructor(apiBaseUrl: string, tokenProvider: () => Promise<string>) {
    this.tokenFn = tokenProvider;

    this.axios = axios.create({
      baseURL: apiBaseUrl,
      headers: { 'Content-Type': 'application/json' },
    });

    this.axios.interceptors.request.use(async (config) => {
      const token = await this.tokenFn();
      config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  }

  async callEndpoint<K extends ApiName>(
    name: K,
    input: ApiInput<K>
  ): Promise<ApiOutput<K>> {
    const def = apiSchema[name];
    return this.call(def.method, def.path, input);
  }

  private async call<TInput, TOutput>(
    method: 'get' | 'post' | 'put' | 'delete',
    path: string,
    data?: TInput
  ): Promise<TOutput> {
    try {
      const response = await this.axios.request<TOutput>({
        method,
        url: path,
        data: ['post', 'put'].includes(method) ? data : undefined,
        params: ['get', 'delete'].includes(method) ? data : undefined,
      });
      return response.data;
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const res = error.response?.data;
        if (status === 401) {
          throw new ApiError(ApiErrorCode.UNAUTHORIZED, 'Unauthorized');
        }
        throw new ApiError(res?.reason, res?.message, res?.details);
      }
      throw error;
    }
  }
}
